import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

// Phones: floating buttons stay out of the way on form-heavy pages and the dense fabric list
const HIDE_ON_MOBILE = ['/consultation', '/quote', '/request-samples', '/contact', '/checkout', '/fabrics'];
const useHideOnRoutes = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.body.classList.toggle('fa-route-hide', HIDE_ON_MOBILE.some((p) => pathname === p || pathname.startsWith(`${p}/`)));
  }, [pathname]);
};

const isField = (el) => el?.matches?.('input:not([type=checkbox]):not([type=radio]), textarea, select, [contenteditable="true"]');

// Phones: floating buttons step aside while someone is typing so they never cover form fields
const useHideWhileTyping = () => {
  useEffect(() => {
    const onIn = (e) => { if (isField(e.target) && !e.target.closest('[data-testid=chat-window]')) document.body.classList.add('fa-input-focus'); };
    const onOut = () => document.body.classList.remove('fa-input-focus');
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => { document.removeEventListener('focusin', onIn); document.removeEventListener('focusout', onOut); };
  }, []);
};

// Phones: floating buttons step aside while a consultation form is on screen,
// or while any page button / form control sits underneath them (bottom-right corner). CSS limits this to mobile.
const FORM_SELECTOR = 'form[data-testid="cms-form-consultation"], [data-testid="consultation-form"]';
const AVOID_SELECTOR = 'a[href], button, input:not([type=hidden]), textarea, select, [role=button], [data-fa-avoid]';
const ZONE = { width: 76, height: 136 };
const TILE_HEIGHT = 240; // large card/image tiles are not treated as buttons
const inZone = (r, vw, vh) => r.width > 0 && r.right > vw - ZONE.width && r.bottom > vh - ZONE.height && r.top < vh && r.left < vw;
const TRANSPARENT = ['rgba(0, 0, 0, 0)', 'transparent'];
// Visible shape: the whole box for filled/outlined controls, otherwise only the text/icons inside it
const hasBox = (el) => {
  const cs = getComputedStyle(el);
  return el.matches('input, textarea, select') || !TRANSPARENT.includes(cs.backgroundColor) || parseFloat(cs.borderTopWidth) > 0 || parseFloat(cs.borderBottomWidth) > 0;
};
const coversZone = (el, vw, vh) => {
  const r = el.getBoundingClientRect();
  if (r.height >= TILE_HEIGHT || !inZone(r, vw, vh)) return false;
  if (hasBox(el)) return true;
  const range = document.createRange();
  range.selectNodeContents(el);
  return [...range.getClientRects()].some((cr) => inZone(cr, vw, vh));
};
const useHideNearForms = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    let forms = [], targets = [], frame = 0;
    const collect = () => {
      const keep = (el) => !el.closest('.floating-action, [data-testid=chat-window], header, [role=dialog]');
      forms = [...document.querySelectorAll(FORM_SELECTOR)].filter(keep);
      targets = [...document.querySelectorAll(AVOID_SELECTOR)].filter(keep);
    };
    const check = () => {
      frame = 0;
      const vw = window.innerWidth, vh = window.innerHeight;
      const hide = vw < 768 && (forms.some((f) => { const r = f.getBoundingClientRect(); return r.bottom > 0 && r.top < vh; })
        || targets.some((el) => coversZone(el, vw, vh)));
      document.body.classList.toggle('fa-form-visible', hide);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(check); };
    const refresh = () => { collect(); schedule(); };
    refresh();
    const mo = new MutationObserver(refresh);
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      mo.disconnect(); cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule);
      document.body.classList.remove('fa-form-visible');
    };
  }, [pathname]);
};

export const WhatsAppButton = () => {
  useHideWhileTyping();
  useHideNearForms();
  useHideOnRoutes();
  const phoneNumber = '17184834598'; // US format without +
  const defaultMessage = encodeURIComponent(
    "Hi! I'm interested in custom window treatments. Can you help me?"
  );
  
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${defaultMessage}`;

  return (
    <motion.a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="floating-action fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 w-12 h-12 md:w-14 md:h-14 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
      data-testid="whatsapp-button"
      title="Chat on WhatsApp"
    >
      <svg 
        viewBox="0 0 24 24" 
        className="w-7 h-7 fill-current"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </motion.a>
  );
};
